import { useEffect } from 'react'
import { buildErrorMessage } from '@constants'
import type { AbstractAgent } from '@copilotkit/react-core/v2'
import { useSnackbar } from '@ui/snackbar'
import { createLiveRunTracker } from '../../utils'

/**
 * Report a turn that broke off (an AG-UI RUN_ERROR, e.g. the runtime's
 * "incomplete stream") as an error snackbar -- but only for a run of this
 * client: connecting to a thread replays its history, including the
 * RUN_ERROR of an earlier failed turn (`createLiveRunTracker`).
 *
 * A subscription to the agent's event stream: the outside world this effect
 * exists for.
 */
const useRunErrorNotifications = (agent: AbstractAgent): void => {
  const { enqueueSnackbar } = useSnackbar()

  useEffect(() => {
    const run = createLiveRunTracker()
    const subscription = agent.subscribe({
      ...run.callbacks,
      onRunErrorEvent: ({ event }) => {
        // A stopped turn is the user's doing, not an error (CopilotKit skips it too).
        if (!run.isLive() || event.code === 'abort') return
        enqueueSnackbar(
          buildErrorMessage(new Error(event.message), 'KI-Chat: Der Assistent wurde unterbrochen'),
          { variant: 'error' },
        )
      },
    })
    return () => subscription.unsubscribe()
  }, [agent, enqueueSnackbar])
}

export default useRunErrorNotifications
